// frontend/src/components/RuralChoiceForm.js

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Navigate } from 'react-router-dom';
import { DndContext, DragOverlay } from '@dnd-kit/core';
import { useAuth } from '../contexts/AuthContext';
import {
  getUserRuralPreferences,
  updateUserRuralPreferences,
  getRuralDepartmentTermCapacities,
  updateUserFlag,
  fetchPreferenceStage,
  savePreferenceStage,
} from '../services/api';
import RuralPreferenceTable from './RuralPreferenceTable';
import RuralHospitalSidebar from './RuralHospitalSidebar';
import LoadingSpinner from './LoadingSpinner';
import OnboardingTour from './OnboardingTour';
import { Button } from '../components/ui/button';
import '../styles/RuralChoiceForm.css';
import DuplicateOverlay from './DuplicateOverlay';
import {
  buildInitialPreferences,
  clonePreferences,
  ensurePreferenceRow,
  convertPreferenceSetToGrid,
  buildPreferencePayloads,
  mergeFixedCells,
  buildPriorityKeyLabel,
  buildCycleKeyLabel,
} from '../utils/preferenceUtils';

// チュートリアルのステップ定義
const TOUR_STEPS = [
  {
    target: '[data-tour="preference-table"]',
    title: '希望登録表へようこそ！',
    content: 'この表で地域医療実習の希望を登録します。各クール（縦軸）と希望順位（横軸）のセルに、希望する病院・診療科をドラッグ＆ドロップで配置してください。',
    placement: 'center',
    disableBeacon: true,
  },
  {
    target: '[data-tour="hospital-sidebar"]',
    title: '病院一覧',
    content: 'こちらに登録可能な病院と診療科の一覧が表示されています。ここから希望する項目を選んでドラッグしてください。',
    placement: 'left',
    disableBeacon: true,
  },
  {
    target: '[data-tour="department-item"]',
    title: '診療科をドラッグ',
    content: 'この診療科名をマウスでクリック＆ホールドして、左側の希望表にドラッグします。',
    placement: 'right',
    disableBeacon: true,
    spotlightPadding: 5,
  },
  {
    target: '[data-tour="preference-cell"]',
    title: 'ここにドロップ',
    content: 'ドラッグした診療科をこのセルにドロップして配置します。希望する順番でセルを埋めていきましょう。',
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: '[data-tour="cycle-header"]',
    title: 'クール設定',
    content: '各クールの優先度と「希望しない」オプションを設定できます。',
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: '[data-tour="priority-select"]',
    title: 'クールの優先順位',
    content: '抽選時にこのクールを何番目に優先するかを設定します。第1順位のクールが最も優先されます。',
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: '[data-tour="exclude-checkbox"]',
    title: '希望しない設定',
    content: 'このクールに配属を希望しない場合はチェックを入れてください。',
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: '.rural-save-button',
    title: '保存を忘れずに！',
    content: '全ての希望を入力したら、必ず「保存する」ボタンを押して登録を完了してください。希望しない以外のクールは第8希望まで入力が必要です。',
    placement: 'top',
    disableBeacon: true,
  },
];

const BASE_PREFERENCE_COUNT = 8;
const CYCLES = Array.from({ length: 7 }, (_, index) => buildCycleKeyLabel(index + 1));
const PREFERENCE_COUNT = BASE_PREFERENCE_COUNT;
const PREFERENCE_KEYS = Array.from({ length: PREFERENCE_COUNT }, (_, index) =>
  buildPriorityKeyLabel(index + 1),
);

const buildDefaultCycleSettings = (cycles) =>
  (cycles || []).reduce((acc, cycle, index) => {
    acc[cycle] = { priority: index + 1, excluded: false };
    return acc;
  }, {});

const cloneCycleSettings = (settings = {}, cycles = []) => {
  const targetCycles = cycles && cycles.length ? cycles : Object.keys(settings || {});
  return targetCycles.reduce((acc, cycle, index) => {
    const source = settings?.[cycle];
    const priority = typeof source?.priority === 'number' ? source.priority : index + 1;
    acc[cycle] = {
      priority,
      excluded: Boolean(source?.excluded),
    };
    return acc;
  }, {});
};

const normalizeCycleSettingsPriorities = (settings = {}, cycles = []) => {
  const next = cloneCycleSettings(settings, cycles);
  const activeCycles = (cycles || []).filter((cycle) => !next?.[cycle]?.excluded);
  const prioritized = activeCycles.map((cycle) => {
    const rawPriority = next?.[cycle]?.priority;
    const fallback = cycles.indexOf(cycle) + 1;
    const numeric = Number(rawPriority);
    const priority = Number.isNaN(numeric) ? fallback : Math.max(1, Math.trunc(numeric));
    return {
      cycle,
      priority,
      order: cycles.indexOf(cycle),
    };
  });

  prioritized.sort((a, b) => a.priority - b.priority || a.order - b.order);

  prioritized.forEach((entry, index) => {
    next[entry.cycle] = {
      ...next[entry.cycle],
      priority: index + 1,
      excluded: false,
    };
  });

  return next;
};

const normalizePreferencesWithSettings = (grid, settings, cycles) => {
  if (!grid || typeof grid !== 'object') {
    return { grid, changed: false };
  }

  const updated = {};
  let changed = false;

  Object.entries(grid).forEach(([rowKey, rowValue]) => {
    if (!rowValue || typeof rowValue !== 'object' || Array.isArray(rowValue)) {
      updated[rowKey] = rowValue;
      return;
    }

    let rowChanged = false;
    const newRow = { ...rowValue };

    (cycles || []).forEach((cycle, index) => {
      const setting = settings?.[cycle] ?? { priority: index + 1, excluded: false };
      const cell = rowValue[cycle];

      if (setting.excluded) {
        if (cell !== '' && cell !== undefined && cell !== null) {
          newRow[cycle] = '';
          rowChanged = true;
        }
        return;
      }

      if (cell && typeof cell === 'object' && !Array.isArray(cell)) {
        const desiredPriority = setting.priority ?? index + 1;
        if (cell.cycle_priority !== desiredPriority) {
          newRow[cycle] = { ...cell, cycle_priority: desiredPriority };
          rowChanged = true;
        }
      }
    });

    if (rowChanged) {
      updated[rowKey] = newRow;
      changed = true;
    } else {
      updated[rowKey] = rowValue;
    }
  });

  return { grid: changed ? updated : grid, changed };
};

const extractCycleSettingsFromGrid = (grid, cycles, defaults) => {
  const settings = cloneCycleSettings(defaults, cycles);
  if (!grid || typeof grid !== 'object') {
    return settings;
  }

  (cycles || []).forEach((cycle, index) => {
    let detectedPriority = null;
    Object.values(grid).some((row) => {
      if (!row || typeof row !== 'object' || Array.isArray(row)) {
        return false;
      }
      const cell = row[cycle];
      if (!cell || typeof cell !== 'object' || Array.isArray(cell)) {
        return false;
      }
      const candidate = cell.cycle_priority ?? cell.payload?.cycle_priority;
      if (candidate === undefined || candidate === null) {
        return false;
      }
      const numeric = Number(candidate);
      if (Number.isNaN(numeric)) {
        return false;
      }
      detectedPriority = Math.min(Math.max(Math.trunc(numeric), 1), cycles.length);
      return true;
    });
    if (detectedPriority !== null) {
      settings[cycle] = {
        priority: detectedPriority,
        excluded: false,
      };
    } else if (!settings[cycle]) {
      settings[cycle] = {
        priority: index + 1,
        excluded: false,
      };
    }
  });
  return settings;
};

const parseCyclePreferencesResponse = (raw, cycles, defaults) => {
  const settings = cloneCycleSettings(defaults, cycles);
  if (!raw || typeof raw !== 'object') {
    return settings;
  }

  const cycleCount = cycles.length || 1;
  Object.entries(raw).forEach(([key, value]) => {
    if (!value || typeof value !== 'object') {
      return;
    }
    const termId = typeof value.term_id === 'number' ? value.term_id : cycles.indexOf(key) + 1;
    const candidateName = cycles.includes(key) ? key : buildCycleKeyLabel(termId || 1);
    if (!cycles.includes(candidateName)) {
      return;
    }
    const priorityRaw = Number(value.priority);
    const priority = Number.isNaN(priorityRaw)
      ? settings[candidateName]?.priority ?? (cycles.indexOf(candidateName) + 1)
      : Math.min(Math.max(Math.trunc(priorityRaw), 1), cycleCount);
    settings[candidateName] = {
      priority,
      excluded: Boolean(value.excluded),
    };
  });

  return settings;
};

const RuralChoiceForm = () => {
  const { user } = useAuth();

  const defaultCycleSettings = useMemo(() => buildDefaultCycleSettings(CYCLES), []);
  const [cycleSettings, setCycleSettings] = useState(() => cloneCycleSettings(defaultCycleSettings, CYCLES));
  const priorityOptions = useMemo(() => Array.from({ length: CYCLES.length }, (_, index) => index + 1), []);
  const defaultCyclePriorities = useMemo(() => {
    const map = {};
    (CYCLES || []).forEach((cycle, index) => {
      map[cycle] = index + 1;
    });
    return map;
  }, []);

  const [hospitals, setHospitals] = useState({});
  const [preferences, setPreferences] = useState(() => buildInitialPreferences(BASE_PREFERENCE_COUNT, CYCLES));
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [activeId, setActiveId] = useState(null);
  const [activeItem, setActiveItem] = useState('');
  const [duplicates, setDuplicates] = useState({});
  const [showDuplicates, setShowDuplicates] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [institutionDepartments, setInstitutionDepartments] = useState([]);
  const [isRegistering, setIsRegistering] = useState(false);
  const [duplicatesExist, setDuplicatesExist] = useState(false);

  const normalizeWithCycleSettings = useCallback(
    (grid, settings = cycleSettings) => normalizePreferencesWithSettings(grid, settings, CYCLES),
    [cycleSettings],
  );

  const updateCycleSettings = useCallback((nextOrUpdater) => {
    setCycleSettings((prev) => {
      const next = typeof nextOrUpdater === 'function' ? nextOrUpdater(prev) : nextOrUpdater || {};
      const normalized = normalizeCycleSettingsPriorities(next, CYCLES);
      setPreferences((current) => {
        const { grid, changed } = normalizePreferencesWithSettings(current, normalized, CYCLES);
        return changed ? grid : current;
      });
      return normalized;
    });
  }, []);

  const findDepartmentTermCapacity = (deptId, instId, termId) => {
    return (institutionDepartments || []).find(
      (dept) =>
        dept.department_id === deptId && dept.institution_id === instId && dept.term_id === termId,
    );
  };

  const formatFetchedPreferences = (fetchedPreferences, departmentCapacities) => {
    const formatted = buildInitialPreferences(BASE_PREFERENCE_COUNT, CYCLES);
    if (
      fetchedPreferences &&
      typeof fetchedPreferences === 'object' &&
      Object.keys(fetchedPreferences).length > 0
    ) {
      Object.entries(fetchedPreferences).forEach(([priority, cycleMap]) => {
        let order = null;
        if (typeof priority === 'string') {
          const match = priority.match(/\d+/);
          if (match) {
            order = Number.parseInt(match[0], 10);
          }
        }
        if (order === null) {
          const numericPriority = Number(priority);
          if (!Number.isNaN(numericPriority) && numericPriority > 0) {
            order = Math.trunc(numericPriority);
          }
        }
        if (!order || order > PREFERENCE_COUNT) {
          return;
        }
        const normalizedPriorityKey = buildPriorityKeyLabel(order);
        const row = ensurePreferenceRow(formatted, normalizedPriorityKey, CYCLES);
        if (cycleMap && typeof cycleMap === 'object') {
          Object.entries(cycleMap).forEach(([cycle, value]) => {
            if (value) {
              const departmentTermCapacity = (departmentCapacities || []).find(
                (dept) =>
                  dept.department_id === value.department_id &&
                  dept.institution_id === value.institution_id &&
                  dept.term_id === value.term_id,
              );

              const departmentTermCapacityId = departmentTermCapacity
                ? departmentTermCapacity.id
                : value.department_term_capacity_id;

              const rawCyclePriority = value.cycle_priority ?? null;
              const parsedCyclePriority =
                rawCyclePriority === null || rawCyclePriority === undefined
                  ? null
                  : Number(rawCyclePriority);
              const cyclePriority = Number.isNaN(parsedCyclePriority) ? null : parsedCyclePriority;

              row[cycle] = {
                id: value.id,
                department_term_capacity_id: departmentTermCapacityId,
                department_id: value.department_id,
                institution_id: value.institution_id,
                display_name: value.display_name,
                capacity: departmentTermCapacity ? departmentTermCapacity.capacity : 0,
                term_id: value.term_id,
                cycle_priority: cyclePriority,
              };
            } else {
              row[cycle] = '';
            }
          });
        }
      });
    }
    return formatted;
  };

  const checkForDuplicates = useCallback(() => {
    const newDuplicates = {};
    Object.entries(preferences).forEach(([preferenceKey, cycleMap]) => {
      Object.entries(cycleMap).forEach(([cycle, value]) => {
        if (value && value.department_term_capacity_id) {
          const id = value.department_term_capacity_id;
          if (!newDuplicates[cycle]) {
            newDuplicates[cycle] = {};
          }
          if (newDuplicates[cycle][id]) {
            newDuplicates[cycle][id].push(preferenceKey);
          } else {
            newDuplicates[cycle][id] = [preferenceKey];
          }
        }
      });
    });

    const duplicatesFound = {};
    let hasDuplicates = false;
    Object.entries(newDuplicates).forEach(([cycle, ids]) => {
      duplicatesFound[cycle] = Object.entries(ids)
        .filter(([, prefs]) => prefs.length > 1)
        .reduce((acc, [id, prefs]) => {
          acc[id] = prefs;
          return acc;
        }, {});
      if (Object.keys(duplicatesFound[cycle]).length > 0) {
        hasDuplicates = true;
      }
    });

    setDuplicates(duplicatesFound);
    setDuplicatesExist(hasDuplicates);
    return hasDuplicates;
  }, [preferences]);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const departmentTermCapacitiesResponse = await getRuralDepartmentTermCapacities();

        if (!Array.isArray(departmentTermCapacitiesResponse)) {
          console.error('Invalid rural department data format:', departmentTermCapacitiesResponse);
          setError('病院の配属データを取得できませんでした。');
          return;
        }

        setInstitutionDepartments(departmentTermCapacitiesResponse);

        const hospitalsData = departmentTermCapacitiesResponse.reduce((acc, item) => {
          if (!acc[item.institution_id]) {
            acc[item.institution_id] = {
              name: item.institution_name,
              departments: [],
            };
          }
          acc[item.institution_id].departments.push({
            id: item.id,
            department_id: item.department_id,
            institution_id: item.institution_id,
            display_name: item.display_name,
            capacity: item.capacity,
            term_id: item.term_id,
            department_term_capacity_id: item.id,
          });
          return acc;
        }, {});

        setHospitals(hospitalsData);

        let formattedPreferences = null;
        let preferencesResponse = null;

        try {
          const unifiedResponse = await fetchPreferenceStage('RURAL');
          if (unifiedResponse?.preference_set) {
            formattedPreferences = convertPreferenceSetToGrid({
              preferenceSet: unifiedResponse.preference_set,
              cycles: CYCLES,
              preferenceCount: PREFERENCE_COUNT,
              departmentCapacities: departmentTermCapacitiesResponse,
            });
          }
        } catch (unifiedError) {
          console.error('Failed to load unified rural preferences:', unifiedError);
        }

        try {
          preferencesResponse = await getUserRuralPreferences(user.uid);
        } catch (legacyError) {
          console.error('Failed to load rural cycle preferences:', legacyError);
        }

        if (!formattedPreferences) {
          if (preferencesResponse?.preferences && Object.keys(preferencesResponse.preferences).length > 0) {
            formattedPreferences = formatFetchedPreferences(
              preferencesResponse.preferences,
              departmentTermCapacitiesResponse,
            );
          }
        }

        if (!formattedPreferences) {
          formattedPreferences = buildInitialPreferences(BASE_PREFERENCE_COUNT, CYCLES);
        }

        const hasCyclePreferences =
          preferencesResponse?.cycle_preferences &&
          Object.keys(preferencesResponse.cycle_preferences).length > 0;
        const derivedSettings = normalizeCycleSettingsPriorities(
          hasCyclePreferences
            ? parseCyclePreferencesResponse(
              preferencesResponse.cycle_preferences,
              CYCLES,
              defaultCycleSettings,
            )
            : extractCycleSettingsFromGrid(
              formattedPreferences,
              CYCLES,
              defaultCycleSettings,
            ),
          CYCLES,
        );
        const { grid: normalizedPreferences } = normalizePreferencesWithSettings(
          formattedPreferences,
          derivedSettings,
          CYCLES,
        );
        setPreferences(normalizedPreferences);
        setCycleSettings(derivedSettings);
      } catch (fetchError) {
        console.error('Failed to fetch rural preference data:', fetchError);
        setError('データの取得に失敗しました。');
      } finally {
        setIsLoading(false);
      }
    };

    if (user) {
      fetchData();
    }
  }, [user, defaultCycleSettings]);

  useEffect(() => {
    checkForDuplicates();
  }, [preferences, checkForDuplicates]);

  const onDragStart = (event) => {
    const { active } = event;
    setActiveId(active.id);
    setActiveItem(active.data.current.value);
  };

  const onDragEnd = (event) => {
    const { active, over } = event;
    setActiveId(null);
    setActiveItem(null);

    if (!over) return;

    const [sourceType, sourcePreference, sourceCycle] = active.id.split('-');
    const [destType, destPreference, destCycle] = over.id.split('-');

    const nextPreferences = clonePreferences(preferences, CYCLES);

    if (sourceType === 'hospital' && destType === 'preference') {
      const destRow = ensurePreferenceRow(nextPreferences, destPreference, CYCLES);

      if (preferences[destPreference]?.[destCycle]?.isFixed) {
        alert('このセルは固定されているため、変更できません。');
        return;
      }

      if (cycleSettings?.[destCycle]?.excluded) {
        alert('配属希望なしにしているタームには追加できません。');
        return;
      }

      const termId = CYCLES.indexOf(destCycle) + 1;
      const departmentTermCapacity = findDepartmentTermCapacity(
        active.data.current.value.department_id,
        active.data.current.value.institution_id,
        termId,
      );

      if (!departmentTermCapacity || departmentTermCapacity.capacity <= 0) {
        alert(`${active.data.current.value.display_name}は残席がありません。別の選択肢をお試しください。`);
        return;
      }

      destRow[destCycle] = {
        id: departmentTermCapacity.id,
        department_term_capacity_id: departmentTermCapacity.id,
        display_name: departmentTermCapacity.display_name,
        capacity: departmentTermCapacity.capacity,
        term_id: termId,
        department_id: departmentTermCapacity.department_id,
        institution_id: departmentTermCapacity.institution_id,
      };
    } else if (sourceType === 'preference' && destType === 'preference') {
      const sourceRow = ensurePreferenceRow(nextPreferences, sourcePreference, CYCLES);
      const destRow = ensurePreferenceRow(nextPreferences, destPreference, CYCLES);

      const sourceValue = sourceRow[sourceCycle];
      const destValue = destRow[destCycle];

      if (sourceValue?.isFixed || destValue?.isFixed) {
        alert('固定されたセルは変更できません。');
        return;
      }

      if (cycleSettings?.[destCycle]?.excluded || cycleSettings?.[sourceCycle]?.excluded) {
        alert('配属希望なしにしているタームとの入れ替えはできません。');
        return;
      }

      if (sourceValue) {
        if (sourceCycle !== destCycle) {
          const newTermId = CYCLES.indexOf(destCycle) + 1;
          const departmentTermCapacity = findDepartmentTermCapacity(
            sourceValue.department_id,
            sourceValue.institution_id,
            newTermId,
          );

          if (!departmentTermCapacity || departmentTermCapacity.capacity <= 0) {
            alert(`${sourceValue.display_name}を${destCycle}に配属できません。別の組み合わせをお試しください。`);
            return;
          }

          const newSourceValue = {
            ...sourceValue,
            department_term_capacity_id: departmentTermCapacity.id,
            capacity: departmentTermCapacity.capacity,
            term_id: newTermId,
          };

          sourceRow[sourceCycle] = destValue;
          destRow[destCycle] = newSourceValue;
        } else {
          sourceRow[sourceCycle] = destValue;
          destRow[destCycle] = sourceValue;
        }
      } else {
        sourceRow[sourceCycle] = destValue;
        destRow[destCycle] = sourceValue;
      }
    } else {
      return;
    }

    const { grid: normalizedPreferences } = normalizeWithCycleSettings(nextPreferences);
    setPreferences(normalizedPreferences);
    checkForDuplicates();
  };

  const handleRemove = (preferenceKey, cycle) => {
    if (preferences[preferenceKey]?.[cycle]?.isFixed) {
      alert('固定されたセルは削除できません。');
      return;
    }
    const nextPreferences = clonePreferences(preferences, CYCLES);
    const row = ensurePreferenceRow(nextPreferences, preferenceKey, CYCLES);
    row[cycle] = '';
    const { grid: normalizedPreferences } = normalizeWithCycleSettings(nextPreferences);
    setPreferences(normalizedPreferences);
    checkForDuplicates();
  };

  const handleDrop = (item, preferenceKey, cycle) => {
    if (preferences[preferenceKey]?.[cycle]?.isFixed) {
      alert('このセルは固定されているため、変更できません。');
      return;
    }

    if (cycleSettings?.[cycle]?.excluded) {
      alert('配属希望なしにしているタームには追加できません。');
      return;
    }

    const nextPreferences = clonePreferences(preferences, CYCLES);
    const row = ensurePreferenceRow(nextPreferences, preferenceKey, CYCLES);

    const termId = CYCLES.indexOf(cycle) + 1;
    const departmentTermCapacity = findDepartmentTermCapacity(
      item.department_id,
      item.institution_id,
      termId,
    );
    const capacity = departmentTermCapacity ? departmentTermCapacity.capacity : 0;

    if (capacity <= 0) {
      alert(`${item.display_name}は残席がありません。別の選択肢をお試しください。`);
      return;
    }

    row[cycle] = {
      id: departmentTermCapacity.id,
      department_term_capacity_id: departmentTermCapacity.id,
      display_name: departmentTermCapacity.display_name,
      capacity,
      term_id: termId,
      department_id: departmentTermCapacity.department_id,
      institution_id: departmentTermCapacity.institution_id,
    };
    const { grid: normalizedPreferences } = normalizeWithCycleSettings(nextPreferences);
    setPreferences(normalizedPreferences);
    checkForDuplicates();
  };

  const handleCyclePriorityChange = useCallback(
    (cycle, value) => {
      updateCycleSettings((prev) => {
        const next = cloneCycleSettings(prev, CYCLES);
        const fallback = defaultCyclePriorities[cycle] ?? (CYCLES.indexOf(cycle) + 1);
        const desiredRaw = Number(value);
        const desired = Number.isNaN(desiredRaw)
          ? fallback
          : Math.min(Math.max(Math.trunc(desiredRaw), 1), CYCLES.length);
        const occupyingCycle = Object.keys(next).find(
          (key) => key !== cycle && !next[key]?.excluded && next[key]?.priority === desired,
        );
        if (occupyingCycle) {
          next[occupyingCycle] = {
            ...next[occupyingCycle],
            priority: next[cycle]?.priority ?? fallback,
          };
        }
        next[cycle] = {
          ...next[cycle],
          priority: desired,
          excluded: false,
        };
        return next;
      });
    },
    [updateCycleSettings, defaultCyclePriorities],
  );

  const handleCycleExclusionChange = useCallback(
    (cycle, excluded) => {
      // 制限追加: 「希望しない」にする際、残り有効ターム数が3未満になるならブロック
      if (excluded) {
        const currentActiveCount = CYCLES.filter((c) => !cycleSettings?.[c]?.excluded).length;
        // 今が3つで、これからさらに1つ減らそうとしている場合 (3 -> 2)
        if (currentActiveCount <= 3) {
          alert('最低でも3つのタームは希望する必要があります。');
          return;
        }
      }

      updateCycleSettings((prev) => {
        const next = cloneCycleSettings(prev, CYCLES);
        const currentPriority = next[cycle]?.priority ?? defaultCyclePriorities[cycle] ?? (CYCLES.indexOf(cycle) + 1);
        next[cycle] = {
          priority: currentPriority,
          excluded,
        };
        return next;
      });
    },
    [updateCycleSettings, defaultCyclePriorities, cycleSettings],
  );

  const validateCyclePrioritySequence = useCallback(() => {
    const activeCycles = CYCLES.filter((cycle) => !cycleSettings?.[cycle]?.excluded);
    const activeCount = activeCycles.length;

    if (activeCount === 0) {
      alert('希望するタームを1つ以上選択してください。');
      return false;
    }

    const priorities = activeCycles.map((cycle) => {
      const raw = cycleSettings?.[cycle]?.priority;
      const numeric = Number(raw);
      return Number.isNaN(numeric) ? null : Math.trunc(numeric);
    });

    const missingPriorityCycles = activeCycles.filter((cycle, index) => priorities[index] === null);
    if (missingPriorityCycles.length > 0) {
      alert('クールの優先順位が未入力です。');
      return false;
    }

    const seen = new Set();
    const duplicates = new Set();
    priorities.forEach((priority) => {
      if (seen.has(priority)) {
        duplicates.add(priority);
      } else {
        seen.add(priority);
      }
    });

    const expected = new Set(Array.from({ length: activeCount }, (_, index) => index + 1));
    const missing = Array.from(expected).filter((value) => !seen.has(value));
    const extras = Array.from(seen).filter((value) => !expected.has(value));

    if (duplicates.size > 0 || missing.length > 0 || extras.length > 0) {
      alert(`クールの優先順位は1〜${activeCount}の連番で重複なく設定してください。`);
      return false;
    }

    return true;
  }, [cycleSettings]);

  const validateRequiredPreferencesFilled = useCallback(() => {
    const requiredCycles = CYCLES.filter((cycle) => !cycleSettings?.[cycle]?.excluded);
    const missingEntries = [];

    requiredCycles.forEach((cycle) => {
      const missingOrders = [];

      PREFERENCE_KEYS.forEach((priorityKey, index) => {
        const row = preferences?.[priorityKey];
        const cell = row && typeof row === 'object' ? row[cycle] : undefined;
        const isFilled =
          cell &&
          typeof cell === 'object' &&
          (cell.department_term_capacity_id || cell.id || cell.payload?.department_term_capacity_id);

        if (!isFilled) {
          missingOrders.push(index + 1);
        }
      });

      if (missingOrders.length > 0) {
        missingEntries.push({ cycle, orders: missingOrders });
      }
    });

    if (missingEntries.length > 0) {
      const detail = missingEntries
        .map(({ cycle, orders }) => {
          const labels = orders.map((order) => `第${order}希望`).join('、');
          return `${cycle}：${labels}`;
        })
        .join('\n');
      alert(`希望しない以外のクールは第8希望まで入力してください。\n未入力：\n${detail}`);
      return false;
    }

    return true;
  }, [preferences, cycleSettings]);

  const handleSave = async () => {
    const hasDuplicates = checkForDuplicates();
    if (hasDuplicates) {
      alert('重複している配属があります。ご確認ください。');
      return;
    }

    const isCyclePriorityValid = validateCyclePrioritySequence();
    if (!isCyclePriorityValid) {
      return;
    }

    const isComplete = validateRequiredPreferencesFilled();
    if (!isComplete) {
      return;
    }

    setIsRegistering(true);
    try {
      const limitedPreferences = PREFERENCE_KEYS.reduce((acc, key) => {
        if (preferences[key]) {
          acc[key] = preferences[key];
        }
        return acc;
      }, {});

      const { unifiedItems, legacyPayload, cyclePayload } = buildPreferencePayloads({
        preferences: limitedPreferences,
        cycles: CYCLES,
        candidateType: 'RURAL',
        userId: user.uid,
        cycleSettings,
      });

      if (unifiedItems.length === 0) {
        alert('最低でも1つの希望を登録してください。');
        setIsRegistering(false);
        return;
      }

      await savePreferenceStage('RURAL', {
        stage_type: 'RURAL',
        items: unifiedItems,
      });

      const response = await updateUserRuralPreferences(user.uid, {
        preferences: legacyPayload,
        cycles: cyclePayload,
      });

      if (response.invalid_preferences && response.invalid_preferences.length > 0) {
        highlightInvalidPreferences(response.invalid_preferences);
        alert('サーバー側で数量情報の不整合が検出されました。ハイライトされたセルをご確認ください。');
      } else {
        alert('配属希望を保存しました。');

        let nextPreferences;
        if (response.preferences && Object.keys(response.preferences).length > 0) {
          nextPreferences = formatFetchedPreferences(response.preferences, institutionDepartments);
        } else {
          nextPreferences = buildInitialPreferences(BASE_PREFERENCE_COUNT, CYCLES);
        }

        const parsedSettings = normalizeCycleSettingsPriorities(
          parseCyclePreferencesResponse(
            response.cycle_preferences,
            CYCLES,
            defaultCycleSettings,
          ),
          CYCLES,
        );

        const merged = mergeFixedCells({
          currentPreferences: preferences,
          nextPreferences,
          cycles: CYCLES,
        });

        const { grid: normalizedMerged } = normalizePreferencesWithSettings(
          merged,
          parsedSettings,
          CYCLES,
        );
        setPreferences(normalizedMerged);
        setCycleSettings(parsedSettings);

        await updateUserFlag(user.uid, 'rural_preference_status', true);
      }
    } catch (error) {
      console.error('Failed to save rural preferences:', error);
      if (error.response && error.response.data && error.response.data.detail) {
        alert('保存に失敗しました：' + JSON.stringify(error.response.data.detail));
      } else {
        alert('保存に失敗しました。お手数ですが、時間をあけてからお試しください。問題が解決しない場合はサポート線までご連絡ください。');
      }
    } finally {
      setIsRegistering(false);
    }
  };

  const highlightInvalidPreferences = (invalidPreferences) => {
    invalidPreferences.forEach((pref) => {
      const { department_term_capacity_id, preference_order } = pref;
      const element = document.querySelector(
        `[data-department-id="${department_term_capacity_id}"][data-preference-order="${preference_order}"]`,
      );
      if (element) {
        element.classList.add('invalid-preference');
      }
    });
  };

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-screen bg-background">
        <LoadingSpinner text="データを読み込んでいます..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center h-screen bg-background">
        <div className="text-accent text-xl" role="alert">{error}</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  return (
    <div className="rural-choice-container rural-choice-form-container">
      {/* オンボーディングツアー */}
      <OnboardingTour
        tourKey="rural-choice"
        steps={TOUR_STEPS}
        isReady={!isLoading && Object.keys(hospitals).length > 0}
      />

      <header className={`rural-choice-header ${isSidebarOpen ? 'sidebar-open' : ''}`}>
        <h2 className="rural-choice-title">地域医療配属希望</h2>
        <button
          type="button"
          className={`rural-choice-tour-button ${isSidebarOpen ? 'sidebar-open' : ''}`}
          onClick={() => {
            try {
              localStorage.removeItem('tour-completed-rural-choice');
            } catch { }
            window['rural-choiceTour']?.startTour?.();
          }}
          title="操作ガイドを再表示"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10"></circle>
            <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path>
            <line x1="12" y1="17" x2="12.01" y2="17"></line>
          </svg>
          <span>操作ガイド</span>
        </button>
      </header>
      <p className="rural-choice-subtitle">希望しないクールを除き、第8希望までご入力ください。</p>
      {Object.keys(hospitals).length === 0 ? (
        <div className="rural-choice-empty-state">病院データがまだ取得されていません。</div>
      ) : (
        <div className="rural-choice-content">
          <DndContext onDragStart={onDragStart} onDragEnd={onDragEnd}>
            <div className={`rural-choice-board ${isSidebarOpen ? 'sidebar-open' : ''}`}>
              <RuralPreferenceTable
                preferences={preferences}
                isSidebarOpen={isSidebarOpen}
                onRemove={handleRemove}
                onDrop={handleDrop}
                duplicates={duplicates}
                departmentTermCapacities={institutionDepartments}
                cycles={CYCLES}
                preferenceOrder={PREFERENCE_KEYS}
                cycleSettings={cycleSettings}
                priorityOptions={priorityOptions}
                onCyclePriorityChange={handleCyclePriorityChange}
                onCycleExclusionChange={handleCycleExclusionChange}
              />
              <RuralHospitalSidebar
                hospitals={hospitals}
                isOpen={isSidebarOpen}
                toggleSidebar={toggleSidebar}
              />
            </div>
            {duplicatesExist && (
              <div className="rural-choice-alert" role="alert">同じ部署を複数回希望しています。重複部位をご確認ください。</div>
            )}
            <div className="rural-choice-actions">
              <Button
                onClick={handleSave}
                className="rural-save-button"
                disabled={isRegistering || duplicatesExist}
              >
                {isRegistering ? '保存中...' : '保存する'}
              </Button>
            </div>
            <DragOverlay>
              {activeId ? (
                <div className="rural-drag-preview">
                  {typeof activeItem === 'object' ? activeItem.display_name : activeItem}
                </div>
              ) : null}
            </DragOverlay>
          </DndContext>
        </div>
      )}
      {showDuplicates && (
        <DuplicateOverlay duplicates={duplicates} onClose={() => setShowDuplicates(false)} />
      )}
    </div>
  );
};

export default RuralChoiceForm;

